import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./containers/App/App";
import * as serviceWorker from "./serviceWorker";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/database";
import config from "./firebase-config.json"; // Config Json File

firebase.initializeApp(config);

// Suppress permission errors in Cursor browser - they're handled by LiveKit PreJoin UI
const originalError = console.error;
const originalWarn = console.warn;
const originalLog = console.log;

// Suppress verbose third-party and dev logs in development
const isVerboseLog = (args: unknown[]): boolean => {
  const msg = args.map(a => typeof a === 'string' ? a : JSON.stringify(a ?? '')).join(' ');
  return (
    msg.includes('already connected to room') ||
    msg.includes('disconnect from room') ||
    msg.includes('componentWillMount has been renamed') ||
    msg.includes('componentWillReceiveProps has been renamed') ||
    msg.includes('React Router Future Flag Warning') ||
    msg.includes('v7_startTransition') ||
    msg.includes('v7_relativeSplatPath') ||
    msg.includes('Download the React DevTools') ||
    msg.includes('API response status') ||
    msg.includes('API response ok') ||
    msg.includes('API response headers') ||
    msg.includes('Unregistered TranscriptionReceived handler') ||
    msg.includes('Unregistered text stream handler') ||
    (msg.includes('WebSocket connection') && msg.includes('failed')) ||
    msg.includes('WebSocket is closed before the connection is established') ||
    msg.includes('Too many active WebGL contexts')
  );
};

// Function to check if error is permission-related
const isPermissionError = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();
  return (
    lowerMessage.includes("permission denied") ||
    lowerMessage.includes("notallowederror") ||
    lowerMessage.includes("permissiondeniederror") ||
    (lowerMessage.includes("getusermedia") && lowerMessage.includes("permission")) ||
    (lowerMessage.includes("microphone") && lowerMessage.includes("permission")) ||
    (lowerMessage.includes("camera") && lowerMessage.includes("permission"))
  );
};

console.error = (...args) => {
  const errorMessage = args.map(a => typeof a === 'string' ? a : String(a)).join(' ');
  if (isPermissionError(errorMessage)) {
    // Silently suppress permission errors - they're handled by the UI
    return;
  }
  // Suppress noisy WebSocket/socket.io errors (e.g. cleanup disconnect)
  if (errorMessage.includes('WebSocket') && (errorMessage.includes('failed') || errorMessage.includes('closed before'))) {
    return;
  }
  originalError.apply(console, args);
};

console.warn = (...args) => {
  const warnMessage = args.join(' ');
  if (isPermissionError(warnMessage)) return;
  if (isVerboseLog(args)) return; // Suppress LiveKit, React deprecations, Router flags
  originalWarn.apply(console, args);
};

console.log = (...args) => {
  if (isVerboseLog(args)) return; // Suppress LiveKit verbose logs
  originalLog.apply(console, args);
};

// Also catch unhandled errors
window.addEventListener('error', (event) => {
  const errorMessage = (event.message || event.error?.message || String(event.error) || '').toLowerCase();
  if (isPermissionError(errorMessage)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }
}, true); // Use capture phase to catch earlier

// Catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const errorMessage = (event.reason?.message || String(event.reason) || '').toLowerCase();
  if (isPermissionError(errorMessage)) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
}, true); // Use capture phase

// Hide React error overlay if it shows permission errors
const hidePermissionErrorOverlay = () => {
  // Get the root element to exclude it from being hidden
  const rootElement = document.getElementById("root");

  // Check for React error overlay - only target actual error overlays, not the app
  const selectors = [
    'div[class*="react-error-overlay"]',
    'div[class*="ErrorOverlay"]',
    'div[id*="react-error-overlay"]',
    '#react-error-overlay',
    'iframe[src*="react-error-overlay"]',
    'iframe[src*="error"]',
  ];

  selectors.forEach(selector => {
    try {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        // Skip if this is the root element
        if (element === rootElement || rootElement?.contains(element)) {
          return;
        }

        const text = (element.textContent || '').toLowerCase();
        const html = (element.innerHTML || '').toLowerCase();
        const combined = text + ' ' + html;

        // Only hide if it's clearly an error overlay (has error-related classes/ids AND permission text)
        const isErrorOverlay = element.id?.includes('error') ||
          (typeof element.className === 'string' && (
            element.className.includes('error') ||
            element.className.includes('overlay')
          ));

        if (isErrorOverlay && isPermissionError(combined)) {
          const htmlElement = element as HTMLElement;
          htmlElement.style.display = 'none';
          htmlElement.style.visibility = 'hidden';
          htmlElement.style.opacity = '0';
          htmlElement.style.pointerEvents = 'none';
          htmlElement.style.position = 'fixed';
          htmlElement.style.top = '-9999px';
          htmlElement.style.left = '-9999px';
          htmlElement.style.zIndex = '-1';
        }
      });
    } catch (e) {
      // Ignore selector errors
    }
  });

  // Check direct children of body, but exclude root and only target error overlays
  Array.from(document.body.children).forEach((child) => {
    // Never hide the root element
    if (child === rootElement) {
      return;
    }

    // Only check elements that look like error overlays
    const isErrorOverlay = (child.id && child.id.includes('error')) ||
      (typeof child.className === 'string' && (
        child.className.includes('error') ||
        child.className.includes('overlay') ||
        child.className.includes('ErrorOverlay')
      )) ||
      child.tagName === 'IFRAME';

    if (isErrorOverlay) {
      const text = (child.textContent || '').toLowerCase();
      const html = (child.innerHTML || '').toLowerCase();
      if (isPermissionError(text + ' ' + html)) {
        const htmlElement = child as HTMLElement;
        htmlElement.style.display = 'none';
        htmlElement.style.visibility = 'hidden';
        htmlElement.style.opacity = '0';
        htmlElement.style.pointerEvents = 'none';
      }
    }
  });
};

// Run immediately and on DOM changes
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hidePermissionErrorOverlay);
} else {
  hidePermissionErrorOverlay();
}

// Use MutationObserver to hide error overlay when it appears
const observer = new MutationObserver((mutations) => {
  // Check immediately when DOM changes
  hidePermissionErrorOverlay();

  // Also check newly added nodes, but only if they're error overlays
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const rootElement = document.getElementById("root");

        // Never hide the root element or anything inside it
        if (element === rootElement || rootElement?.contains(element)) {
          return;
        }

        // Only hide if it's clearly an error overlay
        const isErrorOverlay = (element.id && element.id.includes('error')) ||
          (typeof element.className === 'string' && (
            element.className.includes('error') ||
            element.className.includes('overlay') ||
            element.className.includes('ErrorOverlay')
          )) ||
          element.tagName === 'IFRAME';

        if (isErrorOverlay) {
          const text = (element.textContent || '').toLowerCase();
          const html = (element.innerHTML || '').toLowerCase();
          if (isPermissionError(text + ' ' + html)) {
            element.style.display = 'none';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
            element.style.pointerEvents = 'none';
          }
        }
      }
    });
  });
});

// Start observing as soon as possible
if (document.body) {
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: false,
    characterData: false
  });
} else {
  // Wait for body to be available
  const bodyObserver = new MutationObserver(() => {
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      });
      bodyObserver.disconnect();
    }
  });
  bodyObserver.observe(document.documentElement, {
    childList: true
  });
}

// Also run periodically as a fallback (less frequently to avoid performance issues)
setInterval(hidePermissionErrorOverlay, 2000);

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);
root.render(<App />);

// PWA enabled: Service worker provides offline support and faster loading
// Note: Updates to the app require closing all tabs for the new version to activate
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.register();
